import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaBuilding, FaEdit, FaSave, FaTimes, FaSpinner, FaCamera, FaEye,
  FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaGlobe, FaGraduationCap,
  FaUserGraduate, FaUserTie, FaBook, FaStethoscope, FaHospital, FaCertificate
} from "react-icons/fa";
import axiosInstance from "../utils/axiosConfig";
import { useToast } from "../utils/toastContext";
import { formatApiError } from "../utils/apiErrorFormatter";
import { getApiUrl } from "../utils/config";
import { isAdminUser } from "../utils/dashboardRoutes";
import "../styles/ManageCurriculums.css";
import "../styles/ManageRanks.css";
import "../styles/OrganizationPages.css";

const EMPTY_FORM = {
  name: "",
  type: "",
  established_year: "",
  description: "",
  accreditation: "",
  address: { street: "", city: "", country: "" },
  contact: { phone: "", email: "", website: "" },
};

const toStringValue = (value) => (value == null ? "" : String(value));

const mapApiToForm = (data = {}) => ({
  name: toStringValue(data.name),
  type: toStringValue(data.type),
  established_year: data.established_year == null ? "" : String(data.established_year),
  description: toStringValue(data.description),
  accreditation: toStringValue(data.accreditation),
  address: {
    street: toStringValue(data.address?.street),
    city: toStringValue(data.address?.city),
    country: toStringValue(data.address?.country),
  },
  contact: {
    phone: toStringValue(data.contact?.phone),
    email: toStringValue(data.contact?.email),
    website: toStringValue(data.contact?.website),
  },
});

const mapFormToPayload = (formData) => ({
  name: formData.name.trim(),
  type: formData.type.trim(),
  established_year: formData.established_year === "" ? null : Number(formData.established_year),
  description: formData.description.trim(),
  accreditation: formData.accreditation.trim(),
  address: {
    street: formData.address.street.trim(),
    city: formData.address.city.trim(),
    country: formData.address.country.trim(),
  },
  contact: {
    phone: formData.contact.phone.trim(),
    email: formData.contact.email.trim(),
    website: formData.contact.website.trim(),
  },
});

// The hero holds four stats to match the other mc-* pages; the full five-metric
// breakdown lives in the "Platform footprint" strip below it.
const HERO_STATS = [
  { key: "students", label: "Students" },
  { key: "preceptors", label: "Preceptors" },
  { key: "curriculums", label: "Curricula" },
  { key: "institutions", label: "Institutions" },
];

const FOOTPRINT_STATS = [
  { key: "students", label: "Students", icon: <FaUserGraduate /> },
  { key: "preceptors", label: "Preceptors", icon: <FaUserTie /> },
  { key: "curriculums", label: "Curricula", icon: <FaBook /> },
  { key: "specialities", label: "Experiences", icon: <FaStethoscope /> },
  { key: "institutions", label: "Institutions", icon: <FaHospital /> },
];

const normalizeWebsite = (website) => {
  if (!website) return null;
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
};

/**
 * My Organization — every authenticated user can view the deployment's
 * institution profile; admins can edit it and replace the logo.
 *
 * Rendered as a dashboard view (never as a standalone route) so it inherits the
 * surrounding dashboard's navbar and `page-container` shell, exactly like
 * Manage Curricula and Manage Ranks.
 */
const MyOrganizationPage = ({ title = "My Organization", canEdit }) => {
  const { showError, showSuccess } = useToast();
  // Dashboards pass `canEdit` explicitly; fall back to the stored role so the
  // page still behaves correctly if it is ever mounted without the prop.
  const editable = canEdit === undefined ? isAdminUser() : Boolean(canEdit);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [stats, setStats] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [showLogoViewer, setShowLogoViewer] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [savedData, setSavedData] = useState(EMPTY_FORM);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadOrganization = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("/organization");
        if (cancelled) return;
        const mapped = mapApiToForm(response.data || {});
        setFormData(mapped);
        setSavedData(mapped);
        setStats(response.data?.stats || {});
        setLogoUrl(response.data?.logo_url ? getApiUrl(response.data.logo_url) : null);
        setLoadError("");
      } catch (err) {
        if (cancelled) return;
        const message = formatApiError(err, "Failed to load organization details");
        setLoadError(message);
        showError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOrganization();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showLogoViewer) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowLogoViewer(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLogoViewer]);

  const updateRootField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const updateNestedField = (group, field, value) => setFormData((prev) => ({
    ...prev,
    [group]: { ...prev[group], [field]: value },
  }));

  const handleCancel = () => {
    setFormData(savedData);
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = mapFormToPayload(formData);
      const res = await axiosInstance.put("/organization", payload);
      const mapped = mapApiToForm(res.data?.organization || {});
      setFormData(mapped);
      setSavedData(mapped);
      setStats(res.data?.organization?.stats || stats);
      showSuccess("Organization profile updated");
      setEditing(false);
    } catch (err) {
      showError(formatApiError(err, "Failed to update organization details"));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoPick = () => fileInputRef.current?.click();

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const formPayload = new FormData();
    formPayload.append("file", file);
    try {
      setUploadingLogo(true);
      const res = await axiosInstance.post("/organization/logo", formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.organization?.logo_url;
      setLogoUrl(url ? getApiUrl(url) : null);
      showSuccess("Logo updated");
    } catch (err) {
      showError(formatApiError(err, "Failed to upload logo"));
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="mc-page" style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <FaSpinner className="mc-spin" style={{ fontSize: "3rem", color: "#4a90e2", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  const websiteHref = normalizeWebsite(savedData.contact.website);
  const fullAddress = [savedData.address.street, savedData.address.city, savedData.address.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="mc-page">
      <div className="universal-white-container mc-shell">
        <div className="mc-hero org-hero">
          <div className="org-hero__logo-block">
            <div className="org-hero__logo">
              {logoUrl
                ? (
                  <img
                    src={logoUrl}
                    alt={`${savedData.name || "Organization"} logo`}
                    onClick={() => setShowLogoViewer(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowLogoViewer(true);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                    title="Click to view full size"
                  />
                )
                : <FaBuilding />}
            </div>
            {editable && (
              <>
                <button type="button" className="org-hero__logo-btn" onClick={handleLogoPick} disabled={uploadingLogo}>
                  {uploadingLogo ? <FaSpinner className="mc-spin" /> : <FaCamera />}
                  {uploadingLogo ? "Uploading…" : "Change Logo"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={handleLogoChange}
                />
              </>
            )}
          </div>

          <div className="mc-hero__copy">
            <span className="mc-eyebrow">{title}</span>
            <h1>{savedData.name || "Your Organization"}</h1>
            <p>
              {savedData.type || "Institution"}
              {savedData.established_year ? ` • Est. ${savedData.established_year}` : ""}
            </p>
          </div>

          <div className="mc-hero__stats">
            {HERO_STATS.map((item) => (
              <div className="mc-stat" key={item.key}>
                <strong>{stats[item.key] ?? 0}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mc-toolbar">
          <div className="org-toolbar__tags">
            {savedData.accreditation && (
              <span className="mc-status-pill is-status-active">
                <FaCertificate style={{ marginRight: 5 }} /> {savedData.accreditation}
              </span>
            )}
            {savedData.address.country && (
              <span className="mc-status-pill">
                <FaMapMarkerAlt style={{ marginRight: 5 }} /> {savedData.address.country}
              </span>
            )}
            {!editable && (
              <span className="mc-status-pill">
                <FaEye style={{ marginRight: 5 }} /> View only
              </span>
            )}
          </div>

          {editable && (
            editing ? (
              <div className="mc-toolbar-actions">
                <button className="mc-secondary-btn" type="button" onClick={handleCancel} disabled={saving}>
                  <FaTimes /> Cancel
                </button>
                <button className="mc-primary-btn" type="button" onClick={handleSave} disabled={saving}>
                  {saving ? <FaSpinner className="mc-spin" /> : <FaSave />} {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            ) : (
              <div className="mc-toolbar-actions">
                <button className="mc-primary-btn" type="button" onClick={() => setEditing(true)}>
                  <FaEdit /> Edit Organization
                </button>
              </div>
            )
          )}
        </div>

        {loadError ? (
          <div className="mc-empty-state" style={{ marginTop: "1rem" }}>
            <FaBuilding />
            <h3>Unable to load organization</h3>
            <p>{loadError}</p>
          </div>
        ) : !editing ? (
          <>
            <div className="mc-grid-2" style={{ marginTop: "1rem" }}>
              <div className="mc-form-card">
                <h4 className="mc-subcard-title"><FaGraduationCap /> Overview</h4>
                <div className="mc-overview-list">
                  <div><strong>Name</strong><span>{savedData.name || "—"}</span></div>
                  <div><strong>Type</strong><span>{savedData.type || "—"}</span></div>
                  <div><strong>Established</strong><span>{savedData.established_year || "—"}</span></div>
                  <div><strong>Accreditation</strong><span>{savedData.accreditation || "—"}</span></div>
                </div>
                {savedData.description && <p className="org-description">{savedData.description}</p>}
              </div>

              <div className="mc-form-card">
                <h4 className="mc-subcard-title"><FaMapMarkerAlt /> Contact &amp; Address</h4>
                <div className="mc-overview-list">
                  <div>
                    <strong><FaEnvelope /> Email</strong>
                    <span>
                      {savedData.contact.email
                        ? <a className="org-link" href={`mailto:${savedData.contact.email}`}>{savedData.contact.email}</a>
                        : "—"}
                    </span>
                  </div>
                  <div>
                    <strong><FaPhoneAlt /> Phone</strong>
                    <span>
                      {savedData.contact.phone
                        ? <a className="org-link" href={`tel:${savedData.contact.phone}`}>{savedData.contact.phone}</a>
                        : "—"}
                    </span>
                  </div>
                  <div>
                    <strong><FaGlobe /> Website</strong>
                    <span>
                      {websiteHref
                        ? <a className="org-link" href={websiteHref} target="_blank" rel="noopener noreferrer">{savedData.contact.website}</a>
                        : "—"}
                    </span>
                  </div>
                  <div><strong>Address</strong><span>{fullAddress || "—"}</span></div>
                </div>
              </div>
            </div>

            <div className="mc-form-card org-footprint">
              <h4 className="mc-subcard-title"><FaBuilding /> Platform Footprint</h4>
              <div className="mc-card-metrics org-footprint__metrics">
                {FOOTPRINT_STATS.map((item) => (
                  <div className="mc-card-metric" key={item.key}>
                    <span className="org-footprint__icon">{item.icon}</span>
                    <strong>{stats[item.key] ?? 0}</strong>
                    <small>{item.label}</small>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="mc-editor-panel mc-transition-up" style={{ marginTop: "1rem" }}>
            <div className="mc-grid-2">
              <div className="mc-form-card">
                <h4 className="mc-subcard-title"><FaGraduationCap /> Overview</h4>
                <label className="mc-field">Name
                  <input value={formData.name} onChange={(e) => updateRootField("name", e.target.value)} placeholder="e.g. Qatar University" />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Type
                  <input value={formData.type} onChange={(e) => updateRootField("type", e.target.value)} placeholder="e.g. University" />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Established Year
                  <input
                    type="number"
                    min="0"
                    value={formData.established_year}
                    onChange={(e) => updateRootField("established_year", e.target.value)}
                    placeholder="e.g. 1977"
                  />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Accreditation
                  <input value={formData.accreditation} onChange={(e) => updateRootField("accreditation", e.target.value)} placeholder="e.g. ACPE Certified" />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Description
                  <textarea rows={4} value={formData.description} onChange={(e) => updateRootField("description", e.target.value)} placeholder="A short description shown to everyone in your organization." />
                </label>
              </div>

              <div className="mc-form-card">
                <h4 className="mc-subcard-title"><FaMapMarkerAlt /> Contact &amp; Address</h4>
                <label className="mc-field">Email
                  <input type="email" value={formData.contact.email} onChange={(e) => updateNestedField("contact", "email", e.target.value)} placeholder="contact@organization.edu" />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Phone
                  <input value={formData.contact.phone} onChange={(e) => updateNestedField("contact", "phone", e.target.value)} placeholder="+974 0000 0000" />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Website
                  <input value={formData.contact.website} onChange={(e) => updateNestedField("contact", "website", e.target.value)} placeholder="www.organization.edu" />
                </label>
                <label className="mc-field" style={{ marginTop: "0.85rem" }}>Street
                  <input value={formData.address.street} onChange={(e) => updateNestedField("address", "street", e.target.value)} />
                </label>
                <div className="mc-grid-2" style={{ marginTop: "0.85rem" }}>
                  <label className="mc-field">City
                    <input value={formData.address.city} onChange={(e) => updateNestedField("address", "city", e.target.value)} />
                  </label>
                  <label className="mc-field">Country
                    <input value={formData.address.country} onChange={(e) => updateNestedField("address", "country", e.target.value)} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>

      {showLogoViewer && logoUrl && ReactDOM.createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${savedData.name || "Organization"} logo, full size`}
          onClick={() => setShowLogoViewer(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 20000,
            background: "rgba(15, 23, 42, 0.82)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            boxSizing: "border-box",
            cursor: "zoom-out"
          }}
        >
          <button
            type="button"
            onClick={() => setShowLogoViewer(false)}
            aria-label="Close"
            style={{
              position: "fixed",
              top: "1.25rem",
              right: "1.5rem",
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "1.1rem"
            }}
          >
            <FaTimes />
          </button>
          <img
            src={logoUrl}
            alt={`${savedData.name || "Organization"} logo`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "12px",
              boxShadow: "0 24px 60px rgba(0, 0, 0, 0.5)",
              cursor: "default"
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default MyOrganizationPage;
